import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CyberCard from '../components/CyberCard';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiCheckSquare, FiZap, FiBookOpen, FiCode } = FiIcons;

const STREAKS = [
  { id: 1, name: 'Deep Work', icon: FiZap, current: 12, target: 30, unit: 'hrs', color: '#00F0FF' },
  { id: 2, name: 'Reading', icon: FiBookOpen, current: 5, target: 7, unit: 'days', color: '#F4F1EA' },
  { id: 3, name: 'Algorithm Prep', icon: FiCode, current: 8, target: 10, unit: 'sessions', color: '#8A95A5' },
];

const TASKS = [
  { id: 1, title: 'Architect Database Schema', priority: 'High', time: '120m', status: 'pending' },
  { id: 2, title: 'Review PR #442', priority: 'Med', time: '30m', status: 'pending' },
  { id: 3, title: 'Draft Q3 Objectives', priority: 'Low', time: '60m', status: 'pending' },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState(TASKS);

  const toggleTask = (id) => {
    setTasks(tasks.map(t => t.id === id ? { ...t, status: t.status === 'done' ? 'pending' : 'done' } : t));
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header Area */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-8">
        <div>
          <motion.h1 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-3xl md:text-4xl font-light text-[#F4F1EA] mb-2"
          >
            Welcome back, <span className="font-bold text-[#00F0FF]">Operator</span>.
          </motion.h1>
          <p className="text-[#8A95A5] font-mono-cyber text-sm">SYS.STATUS: OPTIMAL LOGIC STATE ACHIEVED.</p>
        </div>
        <button 
          onClick={() => navigate('/cockpit')}
          className="group flex items-center gap-3 bg-[#00F0FF]/10 border border-[#00F0FF] text-[#00F0FF] px-6 py-3 rounded-lg hover:bg-[#00F0FF]/20 transition-all laser-glow"
        >
          <SafeIcon icon={FiPlay} className="group-hover:scale-110 transition-transform" />
          <span className="font-bold tracking-widest text-sm uppercase">Initialize Session</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Streak Rails */}
        <CyberCard title="Streak Rails" className="lg:col-span-2">
          <div className="space-y-6">
            {STREAKS.map((streak, i) => (
              <motion.div 
                key={streak.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="relative"
              >
                <div className="flex justify-between items-end mb-2">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={streak.icon} style={{ color: streak.color }} />
                    <span className="text-[#F4F1EA] font-medium">{streak.name}</span>
                  </div>
                  <span className="text-xs font-mono-cyber" style={{ color: streak.color }}>
                    {streak.current} / {streak.target} {streak.unit}
                  </span>
                </div>
                {/* Progress Rail */}
                <div className="h-2 w-full bg-[#1A233A] rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${(streak.current / streak.target) * 100}%` }}
                    transition={{ duration: 1, ease: "easeOut", delay: 0.2 + (i * 0.1) }}
                    className="h-full rounded-full"
                    style={{ backgroundColor: streak.color, boxShadow: `0 0 10px ${streak.color}80` }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </CyberCard>

        {/* Quick Stats */}
        <CyberCard title="Session Integrity" className="flex flex-col justify-center items-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#00F0FF]/5 rounded-full blur-3xl" />
          <div className="text-center z-10">
            <div className="text-6xl font-mono-cyber font-bold text-[#00F0FF] mb-2 drop-shadow-[0_0_15px_rgba(0,240,255,0.5)]">
              94<span className="text-2xl text-[#8A95A5]">%</span>
            </div>
            <p className="text-[#8A95A5] text-sm uppercase tracking-widest">Focus Score</p>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-4 w-full">
            <div className="bg-[#050914]/50 p-3 rounded-lg border border-[#1A233A] text-center">
              <div className="text-[#F4F1EA] font-mono-cyber text-lg">4h 12m</div>
              <div className="text-[10px] text-[#8A95A5] uppercase">Deep Work</div>
            </div>
            <div className="bg-[#050914]/50 p-3 rounded-lg border border-[#1A233A] text-center">
              <div className="text-[#F4F1EA] font-mono-cyber text-lg">2</div>
              <div className="text-[10px] text-[#8A95A5] uppercase">Interruptions</div>
            </div>
          </div>
        </CyberCard>

        {/* Task Stack */}
        <CyberCard title="Task Stack" className="lg:col-span-3">
          <div className="space-y-3">
            {tasks.map((task) => (
              <div 
                key={task.id} 
                onClick={() => toggleTask(task.id)}
                className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg border cursor-pointer transition-all ${
                  task.status === 'done' 
                    ? 'bg-[#050914]/30 border-[#1A233A] opacity-50' 
                    : 'bg-[#0A0F1D] border-[#1A233A] hover:border-[#00F0FF]/50'
                }`}
              >
                <div className="flex items-center gap-4 mb-2 sm:mb-0">
                  <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                    task.status === 'done' ? 'bg-[#00F0FF] border-[#00F0FF]' : 'border-[#8A95A5]'
                  }`}>
                    {task.status === 'done' && <SafeIcon icon={FiCheckSquare} className="text-[#050914] text-xs" />}
                  </div>
                  <span className={`font-medium ${task.status === 'done' ? 'text-[#8A95A5] line-through' : 'text-[#F4F1EA]'}`}>
                    {task.title}
                  </span>
                </div>
                
                <div className="flex items-center gap-3 ml-9 sm:ml-0">
                  <span className={`text-xs px-2 py-1 rounded font-mono-cyber ${
                    task.priority === 'High' ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                    task.priority === 'Med' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20' :
                    'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                  }`}>
                    {task.priority}
                  </span>
                  <span className="text-xs text-[#8A95A5] font-mono-cyber flex items-center gap-1">
                    <SafeIcon icon={FiZap} className="text-[10px]" /> {task.time}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CyberCard>
      </div>
    </div>
  );
}